<template>
  <div>
    <b-row>
      <b-col
        align="right"
      >
        <b-button
          variant="outline-primary"
          class="mb-1 mr-2"
          :disabled="loading"
          @click="fetchItems"
        >
          Refresh
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-1"
          :disabled="loading || orderPlacing || duplicating || selectedPob.length <= 0"
          @click="duplicateHandler"
        >
          Duplicate
          <b-spinner
            v-if="duplicating"
            small
            class="ml-1"
          />
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          :disabled="loading || orderPlacing || selectedPob.length <= 0"
          @click="placeOrderHandler"
        >
          Place Order In Marg
          <b-spinner
            v-if="orderPlacing"
            small
            class="mr-1"
          />
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          @click="showPobForm = true"
        >
          Add POB
        </b-button>
      </b-col>
    </b-row>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <!-- Horizontal scroll wrapper -->
    <div
      v-if="!loading && !loadingError"
      class="table-scroll-wrapper"
    >
      <b-table
        :items="results"
        :fields="tableFields"
        responsive="sm"
        striped
        hover
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        class="compact-table"
        no-local-sorting
        @sort-changed="sortingChanged"
      >
        <template #head(select)>
          <b-form-checkbox
            v-model="isAllPobSelected"
            :disabled="results.length === 0"
            @change="handleAllPobSelectChange"
          />
        </template>
        <template #cell(select)="value">
          <b-form-checkbox
            v-model="selectedPob"
            :value="value.item.id"
          />
        </template>
        <template #cell(pob_file)="{item}">
          <a
            v-if="item.pob_file"
            :href="item.pob_file"
            target="_blank"
          >
            <feather-icon
              icon="FileIcon"
              size="20"
              class="cursor-pointer"
            />
          </a>
        </template>
        <template #cell(created_at)="data">
          {{ data.item.created_at && formatedDate(data.item.created_at) }}
        </template>
        <template #cell(order_amount)="data">
          {{ formateNumber(calculateOrderAmount(data.item.pob_items)) }}
        </template>
        <template #cell(pob_items)="data">
          {{ formateNumber(data.item.pob_items.length || 0) }}
        </template>
        <template #cell(status)="{item}">
          <app-chip :variant="getStausChipVariant(item.status)">
            {{ item.status.charAt(0).toUpperCase() + item.status.slice(1) }}
          </app-chip>
        </template>
        <template #cell(order_status)="{item}">
          <app-chip
            v-if="item.order_status"
            :variant="getStausChipVariant(item.order_status)"
          >
            {{ item.order_status.charAt(0).toUpperCase() + item.order_status.slice(1) }}
          </app-chip>
        </template>
        <template #cell(pob_remarks)="data">
          <feather-icon
            v-if="data.item.pob_remarks.length > 0"
            v-b-popover.hover.html.left="{
              content: formatPobRemarks(data.item.pob_remarks),
              title: 'POB Remarks',
              customClass: 'pob-remarks-popover'
            }"
            icon="MessageSquareIcon"
            size="20"
            class="cursor-pointer mr-1"
          />
        </template>
        <template #cell(action)="data">
          <feather-icon
            v-b-tooltip.hover="{boundary:'window'}"
            title="Update Status"
            icon="EditIcon"
            size="20"
            class="cursor-pointer mr-1"
            @click="selectedPobUpdateStatusId = data.item.id"
          />
          <feather-icon
            v-b-tooltip.hover="{boundary:'window'}"
            title="Edit POB"
            icon="Edit2Icon"
            size="20"
            class="cursor-pointer mr-1"
            @click="editPobHandler(data.item.id, data.item.status)"
          />
          <feather-icon
            v-b-tooltip.hover="{boundary:'window'}"
            title="View Details"
            icon="EyeIcon"
            size="20"
            class="cursor-pointer mr-1"
            @click="selectedPobDetailId = data.item.id"
          />
        </template>
      </b-table>
    </div>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />
    <add-pob
      v-if="showPobForm"
      @modal-closed="showPobForm = false"
      @pob-added="addPobHandler"
    />
    <pob-update-model
      v-if="selectedUpdatePobId"
      :id="selectedUpdatePobId"
      @modal-closed="selectedUpdatePobId = null"
      @updated="fetchItems"
    />
    <pob-status-model
      v-if="selectedPobUpdateStatusId"
      :id="selectedPobUpdateStatusId"
      @modal-closed="selectedPobUpdateStatusId = null"
      @updated="fetchItems"
    />
    <pob-details-model
      v-if="selectedPobDetailId"
      :id="selectedPobDetailId"
      @modal-closed="selectedPobDetailId = null"
    />
  </div>

</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, VBTooltip,
  BFormCheckbox,
  VBPopover,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import AppChip from '@/components/UI/AppChip.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddPob from './AddPob.vue'
import DetailedPagination from '../../UI/DetailedPagination.vue'
import PobUpdateModel from './PobUpdateModel.vue'
import PobDetailsModel from './PobDetailsModel.vue'
import PobStatusModel from './PobStatusModel.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
    'b-popover': VBPopover,
  },
  components: {
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    AppChip,
    DetailedPagination,
    AddPob,
    PobUpdateModel,
    PobDetailsModel,
    PobStatusModel,
    BFormCheckbox,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      orderPlacing: false,
      duplicating: false,
      results: [],
      tableFields: [
        { key: 'select' },
        { key: 'pob_number', label: 'POB Number', sortable: true },
        { key: 'company_name', label: 'Company', sortable: true },
        { key: 'created_at', label: 'Created At', sortable: true },
        { key: 'created_by_fullname', label: 'Created By', sortable: true },
        { key: 'pob_file', label: 'Scan Paper', sortable: true },
        { key: 'party_name', label: 'Party', sortable: true },
        {
          key: 'pob_items', label: 'Total Items', sortable: true, class: 'text-right pr-2',
        },
        {
          key: 'order_amount', label: 'Order Amount', sortable: true, class: 'text-right pr-2',
        },
        { key: 'status', label: 'Pob Status', sortable: true },
        { key: 'pob_remarks', label: 'Remark', sortable: true },
        { key: 'order_status', label: 'Order Status', sortable: true },
        { key: 'action', label: 'Action', sortable: true },

      ],
      selectedUpdatePobId: null,
      selectedPobDetailId: false,
      selectedPobUpdateStatusId: null,
      isAllPobSelected: false,
      selectedPob: [],
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      sortBy: 'generation_date',
      sortDesc: true,
      submitting: false,
      showPobForm: false,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    fetchItems() {
      const params = {
        sort_by: this.sortBy,
        sort_desc: this.sortDesc,
        page: this.currentPage,
        per_page: this.perPage,
        ...this.filters,
      }
      this.loading = true
      axios.get(`/marg/pobs?${getParsedQueryString(params)}`)
        .then(res => {
          this.results = res.data.results
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error || error?.response?.data?.detail || 'Something went wrong'
          this.loading = false
        })
    },
    async placeOrderHandler() {
      this.orderPlacing = true

      // Filter results according to selected POBs
      const selectedPobDetails = this.results.filter(pob => this.selectedPob.includes(pob.id))

      // Check for POBs with pending or rejected status
      const invalidPobs = selectedPobDetails.filter(pob => pob.status === 'pending' || pob.status === 'rejected')

      if (invalidPobs.length > 0) {
        this.orderPlacing = false

        const invalidPobIds = invalidPobs.map(pob => pob.pob_number).join(', ')

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Invalid POB Selection',
            text: `Please unselect POB NUMBER(s) ${invalidPobIds} with status pending or rejected`,
            variant: 'warning',
            icon: 'AlertTriangleIcon',
          },
        })
        return
      }

      // Create payload only for valid POBs
      const payload = selectedPobDetails.map(pobDetails => ({
        pob_id: pobDetails.id,
        total_amount: pobDetails.pob_items.reduce((sum, item) => {
          const approvedQty = item.approved_quantity || 0
          const rate = parseFloat(item.rate) || 0
          return +((sum + (approvedQty * rate)).toFixed(2))
        }, 0),
      }))

      // Process orders one by one using reduce
      try {
        await payload.reduce(async (previousPromise, orderItem) => {
          const accumulator = await previousPromise

          const result = await axios.post('/marg/create-pob-order/', orderItem)

          // Show individual success toast for each order
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Order Placed',
              text: `POB ID ${orderItem.pob_id} order placed successfully (Amount: ${this.formateNumber(orderItem.total_amount)})`,
              variant: 'success',
            },
          })

          return [...accumulator, result]
        }, Promise.resolve([]))

        this.orderPlacing = false

        // Clear selected POBs
        this.selectedPob = []
        this.isAllPobSelected = false

        // Refresh the data
        this.fetchItems()
      } catch (error) {
        this.orderPlacing = false

        // Show error toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Order Failed',
            text: error?.response?.data?.error || error?.response?.data?.detail || 'Something went wrong while placing orders',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async duplicateHandler() {
      this.duplicating = true

      // Filter results according to selected POBs
      const selectedPobDetails = this.results.filter(pob => this.selectedPob.includes(pob.id))

      if (selectedPobDetails.length === 0) {
        this.duplicating = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No Selection',
            text: 'Please select POBs to duplicate',
            variant: 'warning',
            icon: 'AlertTriangleIcon',
          },
        })
        return
      }

      // Process duplicates one by one using reduce
      try {
        await selectedPobDetails.reduce(async (previousPromise, pobDetails) => {
          const accumulator = await previousPromise

          // Create FormData for multipart/form-data
          const formData = new FormData()
          // Create payload structure
          const payload = {
            company: pobDetails.company,
            mr: pobDetails.mr,
            party: pobDetails.party,
            pob_mode: pobDetails.pob_mode,
            pob_items: pobDetails.pob_items.map(item => ({
              product: item.product,
              code: item.code || '',
              quantity: item.quantity,
              free_quantity: item.free_quantity,
              rate: item.rate,
            })),
          }

          // Add remark if pob_remarks exist
          if (pobDetails.pob_remarks && pobDetails.pob_remarks.length > 0) {
            payload.pob_remarks = [{
              remarks: `Duplicated from POB Number: ${pobDetails.pob_number}`,
            }]
          }

          formData.append('data', JSON.stringify(payload))

          const result = await axios.post('/marg/create-pob/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Accept: 'application/json',
            },
          })

          // Show individual success toast for each duplicate
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'POB Duplicated',
              text: `POB ${pobDetails.pob_number} duplicated successfully`,
              variant: 'success',
            },
          })

          return [...accumulator, result]
        }, Promise.resolve([]))

        this.duplicating = false

        // Clear selected POBs
        this.selectedPob = []
        this.isAllPobSelected = false

        // Refresh the data
        this.fetchItems()
      } catch (error) {
        this.duplicating = false

        // Show error toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Duplication Failed',
            text: error?.response?.data?.error || error?.response?.data?.detail || 'Something went wrong while duplicating POBs',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    addPobHandler() {
      this.showPobForm = false
      this.currentPage = 1
      this.fetchItems()
    },
    editPobHandler(id, status) {
      if (status === 'pending') {
        this.selectedUpdatePobId = id
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Edit Error',
            text: 'Create Duplicate POB and Edit(Only Pending POB allowed)',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    getStausChipVariant(value) {
      if (value === 'rejected' || value === 'DELETED' || value === 'FAILED') {
        return 'danger'
      }
      if (value === 'pending' || value === 'PICKED') {
        return 'warning'
      }
      if (value === 'fully approved' || value === 'partially approved' || value === 're-approved' || value === 'DELIVERED') {
        return 'success'
      }
      if (value === 'CONFIRMED') {
        return 'primary'
      }

      return 'secondary'
    },
    handleAllPobSelectChange(isChecked) {
      this.selectedPob = isChecked
        ? this.results.map(data => data.id)
        : []
    },
    formatPobRemarks(remarks) {
      if (!remarks || remarks.length === 0) return ''

      return remarks
        .map(remark => {
          const formattedDate = this.formatedDate(remark.created_at, 'DD-MM-YYYY LT')
          return `
        <div style="background: #f8f9fa; border-left: 3px solid #007bff; padding: 8px 12px; margin-bottom: 8px; border-radius: 0 4px 4px 0;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
            <strong style="color: #007bff; font-size: 13px;margin-right: 5px;">${remark.created_by_fullname} </strong>
            <small style="color: #6c757d; font-size: 11px;"> ${formattedDate}</small>
          </div>
          <div style="color: #495057; font-size: 13px; line-height: 1.4;">
            ${remark.remarks}
          </div>
        </div>
      `
        })
        .join('')
    },
    calculateOrderAmount(pobItems) {
      if (!pobItems || !Array.isArray(pobItems)) {
        return 0
      }
      return pobItems.reduce((total, item) => {
        const quantity = parseFloat(item.approved_quantity || item.quantity) || 0
        const rate = parseFloat(item.rate) || 0
        return total + (quantity * rate)
      }, 0)
    },
  },
}
</script>

<style scoped>
/* Horizontal scroll wrapper */
.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

/* Ensure table maintains minimum width */
.table-scroll-wrapper .table {
  min-width: 1200px; /* Adjust this value based on your needs */
  margin-bottom: 0;
}

/* Optional: Style the scrollbar */
.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Remove padding and margin from dropdown button and items */
.action-dropdown /deep/ .btn {
  padding: 0 !important;
  margin: 0 !important;
  border: none;
  background: transparent;
  box-shadow: none !important;
}

.action-dropdown .btn:hover,
.action-dropdown .btn:focus,
.action-dropdown .btn:active {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

/* Adjust dropdown item spacing */
.action-dropdown .dropdown-item {
  padding: 0.5rem 1rem; /* Adjust vertical and horizontal spacing */
  font-size: 0.875rem;
  line-height: 1.2;
}

/* Reduce space between icon and text in dropdown items */
.action-dropdown .dropdown-item .feather {
  margin-right: 0.5rem; /* Adjust icon to text spacing */
}

/* Adjust dropdown menu positioning and spacing */
.action-dropdown .dropdown-menu {
  padding: 0.25rem 0; /* Reduce top/bottom padding of menu */
  min-width: 140px; /* Adjust menu width */
}
</style>
